import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Badge,
  Button,
  Input,
  Spinner, Container,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  getCompanies,
  deleteCompany,
} from "../../slices/companies/thunk";
import DeleteModal from "../../Components/Common/DeleteModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const Companies = () => {
  document.title = "Compañías | Vuelo";

  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const companies = useSelector((state: any) => state.Companies.companyList);
  const isLoading = useSelector((state: any) => state.Companies.isLoading);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [companyToDelete, setCompanyToDelete] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    dispatch(getCompanies());
  }, [dispatch]);

  const handleSelectCompany = useCallback((company: any) => {
    navigate("/company-profile", { state: { company } });
  }, [navigate]);

  const handleAddNewCompany = () => {
    navigate("/company-profile", { state: { isCreating: true } });
  };

  const handleDeleteCompany = (company: any) => {
    setCompanyToDelete(company);
    setDeleteModal(true);
  };

  const onClickDelete = () => {
    if (companyToDelete) {
      dispatch(deleteCompany(companyToDelete.id));
    }
    setDeleteModal(false);
    setCompanyToDelete(null);
  };

  const filteredCompanies = useMemo(() => {
    if (!companies) return [];
    const query = searchQuery.toLowerCase();
    return companies.filter((c: any) =>
      c.businessName?.toLowerCase().includes(query) ||
      c.ruc?.includes(query) ||
      c.tradeName?.toLowerCase().includes(query)
    );
  }, [companies, searchQuery]);

  return (
    <React.Fragment>
      <div className="page-content" style={{ overflowX: "hidden" }}>
        <BreadCrumb title="Compañías" pageTitle="Inicio" />
        <Container fluid>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={onClickDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Row>
          <Col lg={12}>
            <Card className="h-100" id="companyList">
              <CardHeader>
                <div className="d-flex align-items-center">
                  <h5 className="card-title mb-0 flex-grow-1">Compañías</h5>
                  <div className="flex-shrink-0">
                    <Button
                      color="primary"
                      onClick={handleAddNewCompany}
                      disabled={isLoading}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Agregar Compañía
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="p-4">
                {isLoading && (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" className="me-2" />
                    <span className="text-muted">Cargando compañías...</span>
                  </div>
                )}
                {!isLoading && (
                  <React.Fragment>
                    <div className="search-box mb-4">
                      <div className="position-relative">
                        <Input
                          type="text"
                          className="form-control bg-light border-light ps-5 pe-5 py-2"
                          placeholder="Buscar por RUC, nombre comercial o razón social..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{ fontSize: '0.9rem', borderRadius: '0.5rem' }}
                        />
                        <i className="ri-search-line search-icon text-muted"></i>
                        {searchQuery && (
                          <button
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 text-muted p-0"
                            onClick={() => setSearchQuery('')}
                            style={{ fontSize: '1rem' }}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto", overflowX: "hidden", padding: "4px" }}>
                      <Row className="g-3">
                        {filteredCompanies.map((item: any) => (
                          <Col xxl={3} md={4} sm={6} key={item.id}>
                            <Card
                              className="mb-0 border shadow-sm h-100 card-animate"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleSelectCompany(item)}
                            >
                              <CardBody>
                                <div className="d-flex align-items-center mb-3">
                                  <div className="avatar-sm flex-shrink-0 me-3">
                                    {item.logoUrl ? (
                                      <img src={item.logoUrl} alt="" className="img-fluid rounded-circle shadow-sm" />
                                    ) : (
                                      <div className="avatar-title bg-soft-primary text-primary rounded-circle fs-20 shadow-sm">
                                        {item.businessName.charAt(0)}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-grow-1 overflow-hidden">
                                    <h5 className="fs-14 mb-1 text-truncate">{item.businessName}</h5>
                                    <p className="text-muted mb-0 fs-12 text-truncate"><i className="ri-building-4-line me-1 align-bottom"></i> {item.tradeName}</p>
                                  </div>
                                  <div className="flex-shrink-0 ms-2" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        className="btn btn-icon btn-ghost-danger btn-sm"
                                        onClick={() => handleDeleteCompany(item)}
                                    >
                                      <i className="ri-delete-bin-fill fs-16"></i>
                                    </button>
                                  </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-6">
                                        <p className="text-muted mb-1 fs-11 text-uppercase fw-medium">RUC</p>
                                        <h6 className="fs-12 font-monospace mb-0">{item.ruc}</h6>
                                    </div>
                                    <div className="col-6">
                                        <p className="text-muted mb-1 fs-11 text-uppercase fw-medium">Ambiente</p>
                                        <Badge color={item.environmentType === 1 ? "warning" : "success"} pill className={item.environmentType === 1 ? "bg-soft-warning text-black" : "bg-soft-success text-black"}>
                                            {item.environmentType === 1 ? "Pruebas" : "Producción"}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <p className="text-muted mb-1 fs-11 text-uppercase fw-medium">Dirección</p>
                                    <h6 className="fs-12 mb-0 text-truncate" title={item.address}>{item.address}</h6>
                                </div>

                                {item.agentRetentionResolution && (
                                    <div className="mb-3">
                                        <p className="text-muted mb-1 fs-11 text-uppercase fw-medium">Agente de Retención</p>
                                        <h6 className="fs-12 mb-0">{item.agentRetentionResolution}</h6>
                                    </div>
                                )}

                                <div className="d-flex flex-wrap gap-1 mt-auto pt-3 border-top border-light">
                                    <Badge color="info" pill className="bg-soft-info text-black text-uppercase">
                                        {item.rimpeType?.replace('_', ' ')}
                                    </Badge>
                                    {item.obligedAccounting && (
                                        <Badge color="secondary" pill className="bg-soft-secondary text-white">
                                            Contabilidad
                                        </Badge>
                                    )}
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {filteredCompanies.length === 0 && (
                        <div className="text-center py-4 mt-3">
                          <div className="avatar-md mx-auto mb-3">
                            <div className="avatar-title bg-soft-light text-muted rounded-circle fs-24">
                              <i className="ri-search-2-line"></i>
                            </div>
                          </div>
                          <h5 className="text-muted">No se encontraron resultados</h5>
                        </div>
                    )}
                  </React.Fragment>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Companies;
